import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import WaveSurfer from "wavesurfer.js";
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';

import { setSelectedPlayTime } from '@modules/media';
import { setChangedTimelineCutList, updateCut, setEditHistoryFlag, setEditHistory } from '@modules/editor';

import '../../../../../../resources/assets/styles/editor.scss';

function WaveSurferEditor({ wavesurfer, zoomLevel, videoDurationTime }) {
    const dispatch = useDispatch();
    const waveformRef = useRef(null);
    const wsRegionsRef = useRef(null);
    const byCutRowRef = useRef(false);
    const { uuid } = useParams();
    const { t } = useTranslation();

    const [activeRegion, setActiveRegion] = useState(null);
    const activeRegionRef = useRef(null);
    const DEFAULT_REGION_COLOR = "rgba(211, 211, 211, 0.7)";
    const ACTIVE_REGION_COLOR = "rgba(0, 123, 255, 0.3)";

    const playTime = useSelector(state => state.media.playTime);
    const cdn = useSelector(state => state.organization.cdn);
    const cutList = useSelector(state => state.editor.cutList);
    const changedRowCutList = useSelector(state => state.editor.changedRowCutList);
    const editHistoryFlag = useSelector(state => state.editor.editHistoryFlag);
    const [spriteData, setSpriteData] = useState(null);
    const [spriteMatrix, setSpriteMatrix] = useState([]);
    const [wrapperWidth, setWrapperWidth] = useState(0);

    const formWaveSurferOptions = () => ({
        container: waveformRef.current,
        waveColor: "rgb(180, 222, 245, 0.5)",
        progressColor: "#99ccff",
        cursorColor: "#4b89dc",
        width: waveformRef.current ? waveformRef.current.clientWidth : 800,
        height: 200,
        normalize: true,
        minPxPerSec: zoomLevel,
        responsive: true,
        dragToSeek: true,
        scrollParent: false,
        plugins: [TimelinePlugin.create()],
    });

    const setupWaveSurferEvents = () => {
        wavesurfer.current.on("ready", () => {
            const scrollElement = wavesurfer.current.renderer.scrollContainer;
            scrollElement.addEventListener("wheel", event => {
                event.preventDefault();
                event.stopPropagation();
                scrollElement.scrollLeft += event.deltaY;
            });

            setWrapperWidth(wavesurfer.current.renderer.wrapper.offsetWidth);

            wavesurfer.current.on("click", time => {
                dispatch(setSelectedPlayTime(time * videoDurationTime));
            });
        });

        setupRegionEvents();
    };

    const setupRegionEvents = () => {
        wsRegionsRef.current.on("region-in", (region) => {
            activeRegionRef.current = region.id;
            region.setOptions({ color: ACTIVE_REGION_COLOR });
        });

        wsRegionsRef.current.on("region-out", (region) => {
            const currentActiveRegion = activeRegionRef.current;
            if (currentActiveRegion === region.id) {
                region.setOptions({ color: DEFAULT_REGION_COLOR });
                setActiveRegion(null);
            }
        });

        wsRegionsRef.current.on("interaction", () => {
            setActiveRegion(null);
            wavesurfer.current.play();
        });

        wsRegionsRef.current.on("region-update", (region) => {
            const regions = wsRegionsRef.current.getRegions();
            const regionKeys = Object.keys(regions);

            let newStart = region.start;
            let newEnd = region.end;
            const originalLength = region.end - region.start;

            for (const key of regionKeys) {
                const currentRegion = regions[key];

                if (currentRegion.id !== region.id) {
                    if (
                        (newStart < currentRegion.end && newEnd > currentRegion.start) ||
                        (newStart < currentRegion.start && newEnd > currentRegion.end)
                    ) {
                        if (newEnd > currentRegion.start && newStart < currentRegion.start) {
                            newEnd = currentRegion.start;
                            newStart = newEnd - originalLength;
                        } else if (newStart < currentRegion.end && newEnd > currentRegion.end) {
                            newStart = currentRegion.end;
                            newEnd = newStart + originalLength;
                        }

                        region.setOptions({
                            start: newStart,
                            end: newEnd
                        });
                    }
                }
            }
        });

        wsRegionsRef.current.on("region-updated", (region) => {
            if (!byCutRowRef.current) {
                const regionsList = Object.values(wsRegionsRef.current.getRegions());
                const regionIndex = regionsList.findIndex(r => r.id === region.id);

                if (regionIndex !== -1) {
                    const changedObj = {
                        from: region.start,
                        to: region.end,
                        content: region.content?.innerText || '',
                        regionIndex
                    };
                    dispatch(updateCut(changedObj));

                    const updatedCutList = regionsList.map((cut, index) =>
                        index === regionIndex
                            ? changedObj
                            : {
                                from: cut.start,
                                to: cut.end,
                                content: cut.content?.innerText || '',
                                regionIndex: index
                            }
                    );

                    dispatch(setEditHistoryFlag(1));
                    //dispatch(setEditHistory(updatedCutList));
                }
            }
        });
    };

    useEffect(() => {
        const fetchSpriteData = async () => {
            try {
                const response = await fetch(`${cdn}/${uuid}/extData/spriteData.json`);
                if (response.status === 200) {
                    const data = await response.json();
                    setSpriteData(data);
                    setSpriteMatrix(generateSpriteMatrix(data));
                }
            } catch (error) {
                console.error("Error fetching sprite data:", error);
            }
        };

        if (cdn && uuid) fetchSpriteData();
    }, [cdn, uuid]);

    useEffect(() => {
        if (wavesurfer.current && waveformRef.current) {
            wavesurfer.current.zoom(zoomLevel);
        }
    }, [zoomLevel]);

    useEffect(() => {
        if (wavesurfer.current && spriteData && spriteMatrix.length > 0 && wrapperWidth > 0) {
            const interval = zoomLevel <= 0 ? 10 : zoomLevel <= 25 ? 5 : zoomLevel <= 50 ? 2 : zoomLevel < 75 ? 1.5 : 1;
            displaySprites(spriteData, wrapperWidth, spriteMatrix, interval);
        }
    }, [spriteData, spriteMatrix, wrapperWidth, zoomLevel]);

    const generateSpriteMatrix = (data) => {
        const thumbnailsPerSprite = Math.ceil(data.thumbnailCount / data.spriteCount);
        const spriteSide = Math.ceil(Math.sqrt(thumbnailsPerSprite));
        const matrix = [];

        for (let i = 0; i < data.thumbnailCount; i++) {
            const imgIdx = Math.floor(i / (spriteSide * spriteSide)) + 1;
            const posInSprite = i % (spriteSide * spriteSide);
            const x = (posInSprite % spriteSide) * data.thumbnailWidth;
            const y = Math.floor(posInSprite / spriteSide) * data.thumbnailHeight;
            const xPercent = -1 * (x / data.thumbnailWidth) * 100;
            const yPercent = -1 * (y / data.thumbnailHeight) * 100;

            matrix.push({ imgIdx, xPercent, yPercent });
        }
        return matrix;
    };

    const displaySprites = (data, wrapperWidth, matrix, interval) => {
        if (!data || !wrapperWidth || !wavesurfer.current) return;

        const waveformWrapper = wavesurfer.current.renderer.getWrapper();
        const widthPer2Second = (2 * wrapperWidth) / (videoDurationTime || 1);
        const heightPer2Second = widthPer2Second * (data.thumbnailHeight / data.thumbnailWidth);

        const existingWrapper = waveformWrapper.querySelector('.wrapper-sprite');
        if (existingWrapper) existingWrapper.remove();

        const spriteWrapper = document.createElement('div');
        spriteWrapper.className = 'wrapper-sprite';
        spriteWrapper.style.display = 'flex';
        spriteWrapper.style.position = 'absolute';
        spriteWrapper.style.top = 'calc(50% - 40px)';
        waveformWrapper.appendChild(spriteWrapper);

        for (let second = 0; second < Math.floor(videoDurationTime); second += interval) {
            const spriteDiv = document.createElement('div');
            spriteDiv.className = 'wrapper-thumbnail';
            spriteDiv.style.width = `${widthPer2Second}px`;
            spriteDiv.style.height = `${heightPer2Second}px`;

            const matrixIndex = Math.min(Math.floor(second / 10), matrix.length - 1);
            const { imgIdx, xPercent, yPercent } = matrix[matrixIndex];

            spriteDiv.style.backgroundImage = `url(${cdn}/${uuid}/extData/sprite/sprite-${imgIdx}.jpg)`;
            spriteDiv.style.backgroundSize = '300% 300%';
            spriteDiv.style.backgroundPosition = `${xPercent}% ${yPercent}%`;

            spriteWrapper.appendChild(spriteDiv);
        }
    };

    // WaveSurfer 초기화
    useEffect(() => {
        const fetchPeaks = async () => {
            let peaksData;
            try {
                const response = await fetch(`${cdn}/${uuid}/extData/${uuid}.peaks`);
                if (!response.ok) throw new Error("Invalid response");
                peaksData = await response.json();
            } catch (error) {
                const samplesPerSecond = 100;
                const totalSamples = Math.ceil(videoDurationTime * samplesPerSecond);
                peaksData = new Array(totalSamples).fill(0).map(() => (Math.random() * 0.6 - 0.3));
            }

            if (wavesurfer.current) {
                wavesurfer.current.destroy();
            }

            const waveSurferOptions = formWaveSurferOptions();
            waveSurferOptions.peaks = peaksData;
            waveSurferOptions.duration = videoDurationTime;

            wavesurfer.current = WaveSurfer.create(waveSurferOptions);
            wsRegionsRef.current = wavesurfer.current.registerPlugin(
                RegionsPlugin.create({ dragSelection: false })
            );

            setupWaveSurferEvents();
        };

        if (videoDurationTime > 0 && waveformRef.current) {
            fetchPeaks();
        }

        const handleResize = () => {
            if (!waveformRef.current || !wavesurfer.current) return;

            const newWidth = waveformRef.current.clientWidth;

            // 현재 peaks와 currentTime 저장
            const currentPeaks = wavesurfer.current.options.peaks;
            const currentTime = wavesurfer.current.getCurrentTime?.();

            // destroy 후 재생성
            wavesurfer.current.destroy();

            const newOptions = formWaveSurferOptions();
            newOptions.peaks = currentPeaks;
            newOptions.duration = videoDurationTime;

            wavesurfer.current = WaveSurfer.create(newOptions);
            wsRegionsRef.current = wavesurfer.current.registerPlugin(RegionsPlugin.create());

            // 이전 위치로 시크바 이동
            if (currentTime) {
                wavesurfer.current.seekTo(currentTime / videoDurationTime);
            }

            setupWaveSurferEvents();

            // wrapperWidth 업데이트로 썸네일 재생성 트리거
            setWrapperWidth(newWidth);
        };
        console.log('videoDurationTime', videoDurationTime);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [videoDurationTime]); // cutList 제거

    // Region 업데이트 로직을 useCallback으로 최적화
    const updateRegions = useCallback(() => {
        const regions = wsRegionsRef.current.getRegions();
        if (Object.keys(regions).length > 0) {
            Object.keys(regions).forEach(id => {
                regions[id].remove();
            });

            const addRegion = (cut, index) => {
                wsRegionsRef.current.addRegion({
                    id: cut.id,
                    start: cut.from,
                    end: cut.to,
                    color: DEFAULT_REGION_COLOR,
                    drag: true,
                    resize: true,
                    content: `${t('vod.manage.editor_tab.cut_region')} ${index + 1}`
                });
            };

            cutList.forEach(addRegion);
        }
        else {
            const addRegion = (cut, index) => {
                wsRegionsRef.current.addRegion({
                    id: cut.id,
                    start: cut.from,
                    end: cut.to,
                    color: DEFAULT_REGION_COLOR,
                    drag: true,
                    resize: true,
                    content: `${t('vod.manage.editor_tab.cut_region')} ${index + 1}`
                });
            };

            cutList.forEach(addRegion);
        }
    }, [cutList, wsRegionsRef.current, wavesurfer.current]);

    // Region 업데이트
    useEffect(() => {
        if (wsRegionsRef.current && wavesurfer.current) {
            if (cutList.length > 0) {
                updateRegions();
            }
            else {
                wsRegionsRef.current.clearRegions();
            }
        }
    }, [cutList, updateRegions, wsRegionsRef.current, wavesurfer.current]);

    useEffect(() => {
        if (changedRowCutList && changedRowCutList.from && wsRegionsRef.current) {
            const regions = wsRegionsRef.current.getRegions();
            const selectedRegion = regions[changedRowCutList.regionIndex];
            console.log('selectedRegion', selectedRegion);
            if (selectedRegion) {
                selectedRegion.setOptions({
                    color: ACTIVE_REGION_COLOR
                });
            }
            console.log('changedRowCutList', changedRowCutList.from);
            wavesurfer.current?.setScrollTime(changedRowCutList.from);
        }
    }, [changedRowCutList]);

    useEffect(() => {
        if (wavesurfer.current && playTime > 0) {
            const waveCurrentTime = wavesurfer.current.getCurrentTime();
            if (Math.abs(playTime - waveCurrentTime) > 0.1) {
                wavesurfer.current.seekTo(playTime / videoDurationTime);
            }
        }
    }, [playTime]);

    return (
        <div id="waveform" ref={waveformRef} />
    );
}

export default WaveSurferEditor;
